import { useCallback, useEffect, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import {
    registerhistorieAbrufen,
    type HistorieAntwort,
} from "../../services/firmendatenService";
import { VERTRAG_FELDER, zusammenfuehren } from "./GesellschaftsvertragDialog";

/**
 * Die Geschichte einer Gesellschaft — aus dem chronologischen Abdruck.
 *
 * Der aktuelle Abdruck zeigt den Stand von heute. Der chronologische zeigt jede
 * Eintragung seit der ersten und beantwortet damit, was sonst offen bleibt:
 * wann die Gesellschaft entstanden ist und was seither mit ihr geschehen ist.
 *
 * **Die Historie geht in kein Feld.** KMVS führt keins dafür — sie steht hier
 * und im Abdruck, der in der Akte liegt. Was ein Feld hat, wird als Vorschlag
 * angeboten: das Gründungsdatum, der Rechtsvorgänger (frühere Rechtsform, nur
 * bei eindeutiger Zuordnung zum Katalog) und der Unternehmensgegenstand.
 *
 * Mit `documentId` wird der schon abgelegte Abdruck ausgewertet — ohne
 * Portalabruf („Öffnen"). Ohne wird er neu geholt.
 */
const useStyles = makeStyles({
    kopfzeile: { fontSize: "13px", color: "#6b5a66", marginBottom: "14px", lineHeight: 1.5 },
    block: {
        ...shorthands.padding("10px", "12px"),
        ...shorthands.borderRadius("9px"),
        backgroundColor: "#f8f4f8",
        marginBottom: "14px",
        fontSize: "13px",
        lineHeight: 1.5,
        color: "#3d2d39",
    },
    beschriftung: { fontSize: "11.5px", fontWeight: 700, color: "#6b5a66" },
    blockZeile: { display: "block", marginTop: "4px" },
    zeitleiste: { maxHeight: "34vh", overflowY: "auto", marginBottom: "12px" },
    ereignis: {
        display: "flex",
        ...shorthands.gap("10px"),
        ...shorthands.padding("9px", "2px"),
        ...shorthands.borderBottom("1px", "solid", "#ece6ec"),
        fontSize: "12.5px",
        lineHeight: 1.5,
    },
    datum: {
        flexShrink: 0,
        width: "82px",
        fontWeight: 700,
        color: "#7d2169",
        fontVariantNumeric: "tabular-nums",
    },
    nachfolge: { color: "#a8620a", fontWeight: 700 },
    zeile: {
        display: "flex",
        alignItems: "flex-start",
        ...shorthands.gap("10px"),
        ...shorthands.padding("11px", "2px"),
        ...shorthands.borderBottom("1px", "solid", "#ece6ec"),
    },
    inhalt: { flexGrow: 1, minWidth: 0 },
    eingabe: {
        width: "100%",
        ...shorthands.padding("8px", "10px"),
        marginTop: "4px",
        ...shorthands.borderRadius("8px"),
        ...shorthands.border("1.5px", "solid", "#ddd3dc"),
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "13.5px",
        color: "#241522",
        backgroundColor: "#fff",
        ":focus": { ...shorthands.borderColor("#7d2169"), outlineStyle: "none" },
    },
    wertFest: {
        display: "block",
        marginTop: "4px",
        fontSize: "13.5px",
        fontWeight: 600,
        color: "#241522",
    },
    vorschau: { display: "block", fontSize: "12px", color: "#6b5a66", marginTop: "5px", lineHeight: 1.45 },
    ergaenzt: { color: "#1f7a4d", fontWeight: 700 },
    hinweis: {
        fontSize: "12.5px",
        color: "#7a5a18",
        backgroundColor: "#fdf6e3",
        ...shorthands.padding("8px", "10px"),
        ...shorthands.borderRadius("8px"),
        marginTop: "10px",
        lineHeight: 1.45,
    },
    fehler: { fontSize: "12.5px", color: "#b42318", marginTop: "10px" },
    mitte: { ...shorthands.padding("22px", "0"), textAlign: "center", color: "#6b5a66", fontSize: "13px" },
    verweis: {
        ...shorthands.borderWidth("0"),
        ...shorthands.padding("0"),
        backgroundColor: "transparent",
        color: "#7d2169",
        fontFamily: "inherit",
        fontSize: "inherit",
        fontWeight: 700,
        cursor: "pointer",
        textDecorationLine: "underline",
    },
    knopf: {
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.gap("8px"),
        ...shorthands.padding("9px", "14px"),
        ...shorthands.borderRadius("9px"),
        ...shorthands.border("1.5px", "solid", "#ddd3dc"),
        backgroundColor: "#fff",
        color: "#7d2169",
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "12.5px",
        fontWeight: 700,
        lineHeight: 1,
        cursor: "pointer",
        whiteSpace: "nowrap",
        ":hover": { ...shorthands.borderColor("#cd8fbd"), backgroundColor: "#f8eef5" },
        ":disabled": { color: "#a596a1", cursor: "default", backgroundColor: "#f6f3f5" },
    },
    knopfStark: {
        backgroundColor: "#7d2169",
        color: "#fff",
        ...shorthands.borderColor("#7d2169"),
        ":hover": { backgroundColor: "#591149", ...shorthands.borderColor("#591149") },
        ":disabled": {
            backgroundColor: "#d9c6d4",
            ...shorthands.borderColor("#d9c6d4"),
            color: "#fff",
        },
    },
    fuss: { display: "flex", ...shorthands.gap("10px"), justifyContent: "flex-end" },
});

const tag = (iso: string | null) =>
    iso ? iso.split("-").reverse().join(".") : "";

export function RegisterhistorieDialog({
    contactId,
    documentId,
    nurAnsehen = false,
    onClose,
    onUebernehmen,
    onDokumentOeffnen,
}: {
    contactId: number;
    /** Abgelegten Abdruck auswerten (kein Portalabruf). */
    documentId?: number;
    /**
     * Schloss zu: nur ansehen — keine Vorschläge in die Maske übernehmen (das
     * Popup liegt außerhalb des gesperrten Formulars und würde sonst
     * schreiben).
     */
    nurAnsehen?: boolean;
    onClose: () => void;
    onUebernehmen: (werte: Array<{ feld: string; wert: string }>) => void;
    onDokumentOeffnen?: (dok: { documentId: number; fileName: string; mimeType: string | null }) => void;
}) {
    const s = useStyles();
    const [laeuft, setLaeuft] = useState(true);
    /** Zweite Stufe: Der Abruf ist durch, die Texterkennung läuft noch. */
    const [erkennung, setErkennung] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    const [antwort, setAntwort] = useState<HistorieAntwort | null>(null);
    const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set());
    const [werte, setWerte] = useState<Record<string, string>>({});

    const holen = useCallback(async () => {
        setLaeuft(true);
        setFehler(null);
        try {
            const r = await registerhistorieAbrufen(contactId, setErkennung, documentId);
            setAntwort(r);
            setWerte(Object.fromEntries(r.vorschlaege.map((v) => [v.feld, v.wert])));
            setGewaehlt(
                new Set(
                    r.vorschlaege
                        .filter((v) => !v.vorhanden || v.modus === "ergaenzen")
                        .map((v) => v.feld),
                ),
            );
        } catch (e) {
            setFehler(e instanceof Error ? e.message : String(e));
        } finally {
            setLaeuft(false);
            setErkennung(false);
        }
    }, [contactId, documentId]);

    useEffect(() => {
        void holen();
    }, [holen]);

    const umschalten = (feld: string) =>
        setGewaehlt((alt) => {
            const neu = new Set(alt);
            if (neu.has(feld)) neu.delete(feld);
            else neu.add(feld);
            return neu;
        });

    const uebernehmen = () => {
        if (!antwort) return;
        onUebernehmen(
            antwort.vorschlaege
                .filter((v) => gewaehlt.has(v.feld))
                .map((v) => ({
                    feld: VERTRAG_FELDER[v.feld],
                    wert: zusammenfuehren(v, werte[v.feld] ?? v.wert),
                }))
                .filter((w) => w.wert.trim()),
        );
        onClose();
    };

    const b = antwort?.befund;

    return createPortal(
        <div className="kvp-rd-scrim" onClick={laeuft ? undefined : onClose}>
            <div className="dm" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <div className="dm-head">
                    <div className="dm-head-top">
                        <div className="dm-titles">
                            <div className="dm-kicker">Handelsregister · Chronologischer Abdruck</div>
                            <h2>Gründung und Rechtsform</h2>
                        </div>
                        {!laeuft && (
                            <button className="dm-x" onClick={onClose} aria-label="Schließen">
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round"><path d="M4.5 4.5l9 9M13.5 4.5l-9 9" /></svg>
                            </button>
                        )}
                    </div>
                </div>

                <div className="dm-body">
                    {laeuft && (
                        <div className={s.mitte}>
                            {erkennung
                                ? "Der Abdruck liegt in der Akte — die Texterkennung läuft noch …"
                                : documentId
                                  ? "Der abgelegte Abdruck wird ausgewertet …"
                                  : "Der Abdruck wird beim Registerportal geholt …"}
                        </div>
                    )}

                    {!laeuft && antwort && (
                        <>
                            {antwort.documentId && onDokumentOeffnen && (
                                <div className={s.kopfzeile}>
                                    <button
                                        type="button"
                                        className={s.verweis}
                                        onClick={() =>
                                            onDokumentOeffnen({
                                                documentId: antwort.documentId!,
                                                fileName: antwort.fileName ?? "Abdruck",
                                                mimeType: antwort.mimeType,
                                            })
                                        }
                                    >
                                        Abdruck ansehen
                                    </button>{" "}
                                    — der vollständige Abdruck mit allen Eintragungen, zum Prüfen vor dem
                                    Übernehmen.
                                </div>
                            )}

                            {b && (b.rechtsform || b.stammkapital || b.fruehereRechtsform) && (
                                <div className={s.block}>
                                    {b.rechtsform && (
                                        <span className={s.blockZeile}>
                                            <span className={s.beschriftung}>Rechtsform</span> {b.rechtsform}
                                        </span>
                                    )}
                                    {b.fruehereRechtsform && (
                                        <span className={s.blockZeile}>
                                            <span className={s.beschriftung}>Frühere Rechtsform</span>{" "}
                                            {b.fruehereRechtsform}
                                        </span>
                                    )}
                                    {b.vertragVom && (
                                        <span className={s.blockZeile}>
                                            <span className={s.beschriftung}>Gesellschaftsvertrag vom</span>{" "}
                                            {tag(b.vertragVom)}
                                        </span>
                                    )}
                                    {b.stammkapital && (
                                        <span className={s.blockZeile}>
                                            <span className={s.beschriftung}>Stammkapital (zuletzt)</span>{" "}
                                            {b.stammkapital}
                                        </span>
                                    )}
                                </div>
                            )}

                            {!!b?.historie.length && (
                                <div className={s.zeitleiste}>
                                    {b.historie.map((h, i) => (
                                        <div key={i} className={s.ereignis}>
                                            <span className={s.datum}>{tag(h.datum)}</span>
                                            <span className={h.rechtsnachfolge ? s.nachfolge : undefined}>
                                                {h.rechtsnachfolge ? "Rechtsnachfolge: " : ""}
                                                {h.text}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {nurAnsehen && antwort.vorschlaege.length > 0 && (
                                <div className={s.hinweis}>
                                    Zum Übernehmen in die Maske oben das Schloss öffnen.
                                </div>
                            )}
                            {antwort.vorschlaege.map((v) => {
                                const wert = werte[v.feld] ?? v.wert;
                                return (
                                    <div key={v.feld} className={s.zeile}>
                                        <input
                                            type="checkbox"
                                            checked={!nurAnsehen && gewaehlt.has(v.feld)}
                                            disabled={nurAnsehen}
                                            onChange={() => umschalten(v.feld)}
                                            aria-label={v.bezeichnung}
                                            style={{ marginTop: 4, accentColor: "#7d2169" }}
                                        />
                                        <span className={s.inhalt}>
                                            <span className={s.beschriftung}>{v.bezeichnung}</span>
                                            {v.feld === "oldLegalForm" ? (
                                                // Katalogverweis: der Wert ist eine Id — anzeigen, nicht tippen.
                                                <span className={s.wertFest}>{v.anzeige ?? v.wert}</span>
                                            ) : (
                                            <textarea
                                                className={s.eingabe}
                                                rows={v.feld === "jobOccupation" ? 3 : 1}
                                                value={wert}
                                                readOnly={nurAnsehen}
                                                onChange={(ev) =>
                                                    setWerte((alt) => ({ ...alt, [v.feld]: ev.target.value }))
                                                }
                                            />
                                            )}
                                            {v.begruendung && <span className={s.vorschau}>{v.begruendung}</span>}
                                            {v.vorhanden && (
                                                <span className={s.vorschau}>
                                                    Im Feld steht: „{v.vorhanden}" —{" "}
                                                    {v.modus === "ergaenzen" ? (
                                                        <span className={s.ergaenzt}>wird ergänzt</span>
                                                    ) : (
                                                        "wird ersetzt"
                                                    )}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                );
                            })}

                            {antwort.hinweise.map((h, i) => (
                                <div key={i} className={s.hinweis}>{h}</div>
                            ))}
                        </>
                    )}

                    {fehler && <div className={s.fehler}>{fehler}</div>}
                </div>

                <div className={mergeClasses("dm-foot", s.fuss)}>
                    <button type="button" className={s.knopf} onClick={onClose}>
                        {antwort?.vorschlaege.length && !nurAnsehen ? "Abbrechen" : "Schließen"}
                    </button>
                    {!!antwort?.vorschlaege.length && !nurAnsehen && (
                        <button
                            type="button"
                            className={mergeClasses(s.knopf, s.knopfStark)}
                            disabled={laeuft || gewaehlt.size === 0}
                            onClick={uebernehmen}
                        >
                            {gewaehlt.size} in die Maske übernehmen
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}
